import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Settings, X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from '@/components/LocalizedLink'

export function CookieBanner() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      <AnimatePresence>
        {!showPreferences && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-4 left-4 z-[9999] max-w-sm bg-white rounded-lg shadow-xl border border-slate-200 p-3"
            role="dialog"
            aria-label={t('cookie.title', 'Cookie consent')}
          >
            <p className="text-sm text-slate-600 mb-3 pr-6">
              {t('cookie.message')}{' '}
              <LocalizedLink
                to="cookies"
                className="font-medium text-primary underline hover:opacity-80"
              >
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>
            <div className="flex gap-2">
              <button
                onClick={acceptAll}
                className="flex-1 py-1.5 px-3 text-sm font-semibold rounded-md border-none text-dark cursor-pointer transition-colors bg-primary hover:bg-primary-dark"
              >
                {t('cookie.acceptAll', 'Accetta tutti')}
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 py-1.5 px-3 text-sm font-semibold rounded-md border-2 border-dark text-dark bg-transparent cursor-pointer transition-colors hover:bg-dark/10"
              >
                {t('cookie.rejectAll', 'Rifiuta')}
              </button>
            </div>
            <button
              onClick={() => setShowPreferences(true)}
              className="mt-2 flex items-center justify-center gap-1.5 w-full text-xs text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
            >
              <Settings className="h-3.5 w-3.5" />
              {t('cookie.customize', 'Personalizza')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {showPreferences && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowPreferences(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {t('cookie.preferencesTitle', 'Preferenze Cookie')}
                  </h2>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <p className="text-sm text-slate-600 mb-6">
                  {t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.')}
                </p>

                <div className="space-y-3">
                  {/* Necessary */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{t('cookie.necessary', 'Necessari')}</p>
                      <p className="text-xs text-slate-500">{t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}</p>
                    </div>
                    <span className="text-xs text-slate-400 font-medium">{t('cookie.alwaysOn', 'Sempre attivi')}</span>
                  </div>

                  {/* Analytics */}
                  <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{t('cookie.analytics', 'Analitici')}</p>
                      <p className="text-xs text-slate-500">{t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.analytics}
                      onChange={(e) => setTempPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                      className="h-5 w-5 rounded border-slate-300 cursor-pointer accent-primary"
                    />
                  </label>

                  {/* Marketing */}
                  <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{t('cookie.marketing', 'Marketing')}</p>
                      <p className="text-xs text-slate-500">{t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.marketing}
                      onChange={(e) => setTempPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                      className="h-5 w-5 rounded border-slate-300 cursor-pointer accent-primary"
                    />
                  </label>
                </div>

                <div className="mt-6 flex gap-2 justify-end">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="py-2 px-4 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                  >
                    {t('cookie.cancel', 'Annulla')}
                  </button>
                  <button
                    onClick={() => {
                      savePreferences(tempPrefs)
                      setShowPreferences(false)
                    }}
                    className="py-2 px-4 text-sm font-semibold rounded-md text-dark cursor-pointer transition-colors bg-primary hover:bg-primary-dark"
                  >
                    {t('cookie.savePreferences', 'Salva preferenze')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
